// src/components/layout/Layout.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Briefcase, 
  Clock, 
  Package, 
  FileText, 
  Receipt, 
  Settings, 
  File, 
  LogOut, 
  ChevronDown,
  Wrench,
  Menu, // Für das mobile Menü
  X, // Für den Schließen-Button
  ChevronUp
} from 'lucide-react';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const settingsRef = useRef(null);

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/kunden', label: 'Kunden', icon: Users },
    { path: '/projekte', label: 'Aufträge', icon: Briefcase },
    { path: '/zeiterfassung', label: 'Zeiterfassung', icon: Clock },
    { path: '/materialien', label: 'Materialien', icon: Package },
    { path: '/rapporte', label: 'Rapporte', icon: FileText },
    { path: '/rechnungen', label: 'Rechnungen', icon: Receipt },
    { path: '/offerten', label: 'Offerten', icon: File }
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };
  
  useEffect(() => {
    // Handler zum Schließen des Dropdowns, wenn außerhalb geklickt wird
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
    };

    // Event-Listener hinzufügen/entfernen
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Schließe das mobile Menü, wenn sich die Route ändert
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 text-gray-900">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between glass border-b border-gray-200 px-4 py-3.5 shadow-soft sticky top-0 z-40">
        <h1 className="text-lg font-bold text-primary-600">e-rapport</h1>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="touch-target p-2 rounded-xl hover:bg-gray-100 transition-all active:scale-95"
        >
          {mobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-700" />
          ) : (
            <Menu className="h-6 w-6 text-gray-700" />
          )}
        </button>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar - Desktop */}
        <div className="hidden lg:block w-64 glass border-r border-gray-200 shadow-soft overflow-y-auto">
          <div className="p-6 text-center border-b border-gray-200">
            <h1 className="text-xl font-bold text-primary-600">e-rapport</h1>
            <p className="text-xs text-gray-500 mt-1">by axiena AG</p>
          </div>

          <nav className="p-3 flex flex-col h-[calc(100vh-160px)]">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-3 mb-1 rounded-xl font-medium transition-all ${
                  location.pathname === item.path 
                    ? 'bg-primary-600 text-white shadow-md-soft' 
                    : 'text-gray-700 hover:bg-gray-100 hover:text-primary-700'
                }`}
              >
                <item.icon className="h-5 w-5 mr-3" />
                <span className="text-sm">{item.label}</span>
              </Link>
            ))}

            {/* Einstellungen Dropdown */}
            <div className="relative mt-2">
              <button
                onClick={() => setSettingsOpen(!settingsOpen)}
                className="flex items-center w-full px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-100 transition-all font-medium text-sm"
              >
                <Settings className="h-5 w-5 mr-3" />
                Einstellungen
                {settingsOpen ? <ChevronUp className="ml-auto h-4 w-4" /> : <ChevronDown className="ml-auto h-4 w-4" />}
              </button>

              {settingsOpen && (
                <div className="absolute left-0 mt-1 w-full bg-white rounded-lg border shadow-md py-1 z-10">
                  <Link
                    to="/materials/manage"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setSettingsOpen(false)}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Materialien verwalten
                  </Link>
                  <Link
                    to="/services"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setSettingsOpen(false)}
                  >
                    <Wrench className="h-4 w-4 mr-2" />
                    Dienstleistungen verwalten
                  </Link>
                  <Link
                    to="/unternehmensprofil"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setSettingsOpen(false)}
                  >
                    <File className="h-4 w-4 mr-2" />
                    Unternehmensprofil
                  </Link>
                  <Link
                    to="/employees"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setSettingsOpen(false)}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Mitarbeiter erfassen
                  </Link>
                </div>
              )}
            </div>
          </nav>
          
          <div className="absolute bottom-0 left-0 w-64 p-4 border-t border-gray-200 bg-white">
            <button
              onClick={handleLogout}
              className="btn-modern flex items-center justify-center w-full bg-red-500 hover:bg-red-600 text-white px-4 py-3 shadow-md-soft focus:outline-none focus:ring-2 focus:ring-red-300"
            >
              <LogOut className="h-5 w-5 mr-2" />
              <span className="font-semibold">Abmelden</span>
            </button>
          </div>
        </div>

        {/* Mobile Menu - Slide in from left */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40 flex">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50" 
              onClick={() => setMobileMenuOpen(false)}
            ></div>
            
            {/* Sidebar */}
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white overflow-y-auto shadow-lg-soft">
              
                <nav className="p-4 overflow-y-auto">
                  {menuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`touch-target flex items-center px-4 py-4 mb-2 rounded-xl font-medium transition-all ${
                        location.pathname === item.path 
                          ? 'bg-primary-600 text-white shadow-md-soft' 
                          : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                      }`}
                    >
                      <item.icon className="h-6 w-6 mr-3" />
                      <span className="text-base">{item.label}</span>
                    </Link>
                  ))}
                  
                  {/* Einstellungen auf Mobile als normale Links */}
                  <div className="mt-4 border-t pt-4">
                    <div className="px-4 py-2 text-sm text-gray-500">Einstellungen</div>
                    
                    <Link
                      to="/materials/manage" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center px-4 py-3 mt-1 rounded-md text-gray-700 hover:bg-gray-100"
                    >
                      <Package className="h-5 w-5 mr-3" />
                      Materialien verwalten
                    </Link>
                    
                    <Link
                      to="/services" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center px-4 py-3 mt-1 rounded-md text-gray-700 hover:bg-gray-100"
                    >
                      <Wrench className="h-5 w-5 mr-3" />
                      Dienstleistungen verwalten
                    </Link>
                    
                    <Link
                      to="/unternehmensprofil" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center px-4 py-3 mt-1 rounded-md text-gray-700 hover:bg-gray-100"
                    >
                      <File className="h-5 w-5 mr-3" />
                      Unternehmensprofil
                    </Link>
                    
                    <Link
                      to="/employees" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center px-4 py-3 mt-1 rounded-md text-gray-700 hover:bg-gray-100"
                    >
                      <Users className="h-5 w-5 mr-3" />
                      Mitarbeiter erfassen
                    </Link>
                  </div>
                </nav>

              <div className="sticky bottom-0 left-0 right-0 p-4 bg-white border-t">
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center w-full bg-red-500 hover:bg-red-600 text-white px-4 py-3 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  Abmelden
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-8">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
